import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/PageHeader';
import { useOrganizationStore } from '../../stores/organization.store';
import KnowledgeBaseArticleCard from '../../components/help/KnowledgeBaseArticleCard';
import {
  getKnowledgeArticlesForOrganizationType,
  getTopLevelKnowledgeArticlesForOrganizationType,
} from '../../constants/knowledgeBaseArticles';
import {
  knowledgeArticleMatchesSearch,
  localizeKnowledgeArticles,
} from '../../helpers/knowledge-base-localization.helper';
import { useHelpTypography } from '../../hooks/useHelpTypography';

const KnowledgeBase: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { bodySx } = useHelpTypography();
  const { selectedOrganization } = useOrganizationStore();
  const [search, setSearch] = useState('');

  const organizationArticles = useMemo(() => {
    if (!selectedOrganization) return [];
    return getKnowledgeArticlesForOrganizationType(selectedOrganization.organization_type);
  }, [selectedOrganization]);

  const topLevelArticles = useMemo(() => {
    if (!selectedOrganization) return [];
    return getTopLevelKnowledgeArticlesForOrganizationType(selectedOrganization.organization_type);
  }, [selectedOrganization]);

  const filteredArticles = useMemo(() => {
    const query = search.trim();
    const source = query ? organizationArticles : topLevelArticles;

    return localizeKnowledgeArticles(
      source.filter((article) => knowledgeArticleMatchesSearch(article, query))
    );
  }, [search, organizationArticles, topLevelArticles, i18n.language]);

  if (!selectedOrganization) {
    return (
      <Typography color="text.secondary">{t('knowledgeBase.selectOrganization')}</Typography>
    );
  }

  return (
    <Box>
      <PageHeader
        icon={<MenuBookOutlinedIcon />}
        title={t('knowledgeBase.title')}
        subtitle={t('knowledgeBase.subtitle')}
        footer={
          <TextField
            size="small"
            placeholder={t('knowledgeBase.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              width: '100%',
              maxWidth: 420,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : '#F8FAFC',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
                '& fieldset': {
                  borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : '#E2E8F0',
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.22)' : '#CBD5E1',
                },
                '&.Mui-focused': {
                  bgcolor: theme.palette.background.paper,
                  boxShadow: `0 0 0 3px ${theme.palette.primary.main}1F`,
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: '1px',
                  },
                },
              },
              '& input': {
                py: 1.1,
                ...bodySx,
                color: 'text.primary',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
        }
      />

      {filteredArticles.length === 0 ? (
        <Paper
          elevation={2}
          sx={{
            borderRadius: '16px',
            p: 4,
            textAlign: 'center',
            bgcolor: theme.palette.background.paper,
          }}
        >
          <Typography sx={{ ...bodySx, color: 'text.secondary', mb: 1 }}>
            {t('knowledgeBase.noResultsTitle')}
          </Typography>
          <Typography sx={{ ...bodySx, fontSize: '0.875rem', color: 'text.secondary' }}>
            {t('knowledgeBase.noResultsHint')}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filteredArticles.map((article) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={article.id}>
              <KnowledgeBaseArticleCard
                articleId={article.id}
                title={article.title}
                viewDetailsLabel={t('knowledgeBase.viewDetails')}
                onClick={() => navigate(`/knowledge-base/${article.id}`)}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default KnowledgeBase;
